import type {
    ITelemetryInitializerHandler,
    ITelemetryItem,
} from '@microsoft/applicationinsights-core-js';
import {
    ApplicationInsights,
    IConfig,
    IConfiguration,
    IPageViewTelemetry,
} from '@microsoft/applicationinsights-web';
import useMsalUser from '@shared/hooks/useMsalUser';
import { ErrorDetail, EventDetail, PageViewDetail, TraceDetail } from '@shared/logging/appLogger';
import LoggerTypes from '@shared/types/logger';
import { useCallback, useEffect, useMemo, useRef } from 'react';

interface Props {
    config?: IConfiguration & IConfig;
    debug?: boolean;
}

const generalAIConfig = {
    enableCorsCorrelation: false,
    enableRequestHeaderTracking: true,
    enableResponseHeaderTracking: true,
    enableAjaxErrorStatusText: true,
    enableAjaxPerfTracking: true,
    enableAutoRouteTracking: true,
    maxBatchInterval: 5000,
    correlationHeaderExcludedDomains: ['*.fullstory.com', '*.g.doubleclick.net', '*.qualtrics.com'],
    excludeDependencyTargets: [
        /\.fullstory\.com/,
        /\.launchdarkly\.com/,
        /\.google-analytics\.com/,
        /g\.doubleclick\.net/,
        /\.googlesyndication\.com/,
        /\.qualtrics\.com/,
        /carmax\.tt\.omtrdc\.net/,
        /akstat\.io/,
        /go-mpulse\.net/,
        /chrome-extension:/,
    ],
    excludeMessageSources: ['Launch Darkly'],
};

/**
 * Custom telemetry filtering and enrichment logic for App Insights
 * @param envelope Telemetry item being sent
 * @returns true if envelope should be sent, false to suppress
 */
const CustomTelemetryInitializer = (envelope: ITelemetryItem) => {
    switch (envelope?.baseType) {
        case 'RemoteDependencyData':
            if (
                // Exclude any dependencies based on target
                generalAIConfig.excludeDependencyTargets.some(exclude =>
                    (envelope.baseData.target as string).match(exclude)
                )
            ) {
                return false; // don't sent to app insights
            }
            if (
                // Don't log contentful 404s
                (envelope.baseData.target as string).match(/\.azurefd\.net\/content/) &&
                (envelope.baseData.responseCode as number) == 404
            )
                return false;
            break;
        case 'MessageData':
            if (
                generalAIConfig.excludeMessageSources.some(
                    exclude => envelope.data.source == exclude
                )
            ) {
                return false;
            }
            break;
        case 'PageviewPerformanceData':
        case 'PageviewData': {
            // Add stockNum as custom data point on VDP page views
            const match = (envelope.baseData.uri as string).match(/vehicledetail\/(\d+)/);
            if (match) envelope.data.stockNum = match[1];
            break;
        }
    }
    return true; // Allow telemetry to propagate
};

function AppInsightsListener({ config, debug = false }: Props) {
    const { getUser } = useMsalUser();

    const debugLog = useCallback(
        (message: string) => {
            if (debug) {
                console.warn(message);
            }
        },
        [debug]
    );

    const appInsights = useMemo(() => {
        if (config && config.connectionString) {
            try {
                const appInsights = new ApplicationInsights({
                    config: {
                        ...generalAIConfig,
                        ...config,
                    },
                });

                appInsights.addTelemetryInitializer(CustomTelemetryInitializer);
                appInsights.loadAppInsights();

                return appInsights;
            } catch (e) {
                console.error(e);
            }
        } else {
            debugLog('No connection string was provided for App Insights.');
        }
    }, [config, debugLog]);

    /**
     * Wire current user information to telemetry
     */
    useEffect(() => {
        if (appInsights?.context) {
            const user = getUser();

            if (user) {
                appInsights.setAuthenticatedUserContext(
                    user.consumerResourceId,
                    user.username,
                    true
                );
            } else {
                appInsights.clearAuthenticatedUserContext();
            }
        }
    }, [appInsights, getUser]);

    const remoteNameTelemetry = useRef<ITelemetryInitializerHandler>();
    /**
     * Wire current remote name to telemetry
     */
    const setRemoteName = useCallback(
        (event: CustomEvent) => {
            if (!appInsights) {
                debugLog('App insights is not enabled');
                return;
            }

            if (remoteNameTelemetry.current) {
                remoteNameTelemetry.current.remove();
            }

            remoteNameTelemetry.current = appInsights.addTelemetryInitializer(envelope => {
                envelope.tags['ai.cloud.role'] = event?.detail?.name;
                return true;
            }) as ITelemetryInitializerHandler;
        },
        [appInsights, debugLog]
    );

    const logException = useCallback(
        (event: CustomEvent<ErrorDetail>) => {
            if (!appInsights) {
                debugLog('App insights is not enabled');
                return;
            }

            appInsights.trackException(
                {
                    exception: event?.detail?.error,
                },
                event?.detail?.properties
            );
        },
        [appInsights, debugLog]
    );

    const logTrace = useCallback(
        (event: CustomEvent<TraceDetail>) => {
            if (!appInsights) {
                debugLog('App insights is not enabled');
                return;
            }

            appInsights.trackTrace(
                {
                    severityLevel: event?.detail?.severityLevel ?? 0,
                    message: event?.detail?.message,
                },
                event?.detail?.properties
            );
        },
        [appInsights, debugLog]
    );

    const logEvent = useCallback(
        (event: CustomEvent<EventDetail>) => {
            if (!appInsights) {
                debugLog('App insights is not enabled');
                return;
            }

            appInsights.trackEvent(
                {
                    name: event?.detail?.name,
                },
                event?.detail?.properties
            );
        },
        [appInsights, debugLog]
    );

    const logPageView = useCallback(
        (event: CustomEvent<PageViewDetail>) => {
            if (!appInsights) {
                debugLog('App insights is not enabled');
                return;
            }
            appInsights.trackPageView(event.detail as IPageViewTelemetry);
        },
        [appInsights, debugLog]
    );

    const setFeatureFlagTelemetry = useRef<ITelemetryInitializerHandler>();
    const setFeatureFlag = useCallback(
        (event: CustomEvent) => {
            if (!appInsights) {
                debugLog('App insights is not enabled');
                return;
            }

            if (setFeatureFlagTelemetry.current) {
                setFeatureFlagTelemetry.current.remove();
            }

            setFeatureFlagTelemetry.current = appInsights.addTelemetryInitializer(envelope => {
                envelope.data.featureFlags = event?.detail?.flags;
                return true;
            }) as ITelemetryInitializerHandler;
        },
        [appInsights, debugLog]
    );

    useEffect(() => {
        window.addEventListener(LoggerTypes.EXCEPTION, logException);
        window.addEventListener(LoggerTypes.TRACE, logTrace);
        window.addEventListener(LoggerTypes.EVENT, logEvent);
        window.addEventListener(LoggerTypes.PAGE_VIEW, logPageView);
        window.addEventListener(LoggerTypes.REMOTE_LOADED, setRemoteName);
        window.addEventListener(LoggerTypes.FEATURE_FLAG, setFeatureFlag);
        return () => {
            window.removeEventListener(LoggerTypes.EXCEPTION, logException);
            window.removeEventListener(LoggerTypes.TRACE, logTrace);
            window.removeEventListener(LoggerTypes.EVENT, logEvent);
            window.removeEventListener(LoggerTypes.PAGE_VIEW, logPageView);
            window.removeEventListener(LoggerTypes.REMOTE_LOADED, setRemoteName);
            window.removeEventListener(LoggerTypes.FEATURE_FLAG, setFeatureFlag);
        };
    }, [logException, logTrace, logEvent, logPageView, setRemoteName, setFeatureFlag]);

    return <></>;
}

export default AppInsightsListener;
